// Translator notes: The translator's own notes (engine/src/translator.ts, finnish-parse.ts): word order, cases and forms,
// unknown words, the clause rules of rules/en-clauses.ts and rules/english.ts.
// Each id is an engine note (`notes.add(text, id, args)`); `args` names its arguments in the order the
// strings take them. Collected by translator/notes.ts (import.meta.glob): adding a note means adding it here.
import { defineNotes } from '../note-defs';
import { CASE_IN_FI, CASE_IN_EN } from './words';

export const notes = defineNotes({
  noGloss: {
    args: ['word'],
    fi: (a: string[]) => `Sanalle ${a[0]} ei ole merkitystä, joten se jätettiin valyriaksi.`,
    en: (a) => `${a[0]} has no meaning in the dictionary, so it was kept in Valyrian.`,
  },
  sharedSubject: {
    args: [],
    fi: () => 'Rinnasteisten lauseiden yhteistä subjektia ei toisteta.',
    en: () => 'The shared subject of coordinated clauses is not repeated.',
  },
  pronounAdded: {
    args: [],
    fi: () => 'Käännökseen lisättiin persoonapronomini: valyriassa se jätetään usein pois, koska verbin pääte kertoo persoonan.',
    en: () => 'A subject pronoun was added: Valyrian often leaves it out, because the verb ending gives the person.',
  },
  copula: {
    args: ['verb'],
    fi: (a: string[]) => `Lauseessa ei ole verbiä, joten käännökseen lisättiin ”${a[0] === 'be' ? 'be' : 'olla'}”.`,
    en: (a) => `The clause has no verb, so “${a[0] === 'olla' ? 'olla' : 'be'}” was added.`,
  },
  fiDraft: {
    args: [],
    fi: () => 'Käännöksen suomenkielisten merkitysten joukossa on tarkistamattomia AI-luonnoksia.',
    en: () => 'The Finnish meanings used include unreviewed AI drafts.',
  },
  fiFallback: {
    args: [],
    fi: () => 'Osalla sanoista ei ole vielä suomenkielistä merkitystä, joten englanninkielinen näkyy hakasulkeissa.',
    en: () => 'Some words have no Finnish meaning yet, so the English one is shown in brackets.',
  },
  fiUnsureForms: {
    args: [],
    fi: () => 'Epävarmat suomen muodot näytetään perusmuodossa sijan nimen kanssa, esim. ”talo (inessiivi)”.',
    en: () => 'Unsure Finnish forms are shown as the base form with the case name, e.g. “talo (inessiivi)”.',
  },
  fiNumeral: {
    args: [],
    fi: () => 'Suomessa lukusanan jälkeen substantiivi on yksikössä (”yhdeksän sotilasta”).',
    en: () => 'In Finnish the noun after a numeral is singular (“yhdeksän sotilasta”).',
  },
  fiUnsureVerbs: {
    args: [],
    fi: () => 'Epävarmat suomen verbimuodot näytetään perusmuodossa.',
    en: () => 'Unsure Finnish verb forms are shown as the base form.',
  },
  endingRules: {
    args: ['word'],
    fi: (a: string[]) => `Sana ${a[0]} tunnistettiin vain päätteen perusteella.`,
    en: (a) => `${a[0]} was recognised by its ending only.`,
  },
  lengthening: {
    args: ['word'],
    fi: (a: string[]) => `Sanan ${a[0]} pidentynyt loppuvokaali luettiin sanaksi ”ja”.`,
    en: (a) => `The lengthened final vowel of ${a[0]} was read as “and”.`,
  },
  unknownKept: {
    args: ['words'],
    fi: (a: string[]) =>
      a[0]!.includes(',') ? `Tunnistamattomat sanat jätettiin sellaisinaan: ${a[0]}.` : `Tunnistamaton sana ${a[0]} jätettiin sellaisenaan.`,
    en: (a) => (a[0]!.includes(',') ? `Unrecognised words were kept as they are: ${a[0]}.` : `The unrecognised word ${a[0]} was kept as it is.`),
  },
  relative: {
    args: [],
    fi: () => 'Relatiivilause sijoitettiin ennen sanaa lua ja sen pääsanaa, kuten valyriassa.',
    en: () => 'The relative clause was placed before “lua” and its noun, as in High Valyrian.',
  },
  closestForm: {
    args: ['word'],
    fi: (a: string[]) => `Sanasta ${a[0]} käytettiin lähintä löytynyttä muotoa.`,
    en: (a) => `The closest form found was used for ${a[0]}.`,
  },
  andLengthening: {
    args: [],
    fi: () => '”Ja” kahden substantiivin välissä: valyriassa jälkimmäisen sanan loppuvokaali pitenee (”vala ābrā” = mies ja nainen).',
    en: () => '“And” between two nouns: in Valyrian the last word’s final vowel is lengthened (“vala ābrā” = the man and the woman).',
  },
  pronounDropped: {
    args: [],
    fi: () => 'Persoonapronomini jätettiin pois: valyriassa verbin pääte kertoo persoonan.',
    en: () => 'The subject pronoun was left out: in Valyrian the verb ending shows the person.',
  },
  ilagon: {
    args: [],
    fi: () => '”Olla jossakin” ja ”jossakin on” käännettiin verbillä ilagon.',
    en: () => '“Be somewhere” and “there is” were translated with the verb ilagon.',
  },
  negImperative: {
    args: [],
    fi: () => 'Kielteinen käsky: infinitiivi ja daor (”sōpagon daor” = älä naura).',
    en: () => 'A negative command: the infinitive and daor (“sōpagon daor” = do not laugh).',
  },
  negSubjunctive: {
    args: [],
    fi: () => 'Kielto: verbi on subjunktiivissa ennen sanaa daor (”iksos daor” = ei ole).',
    en: () => 'Negation: the verb is in the subjunctive before daor (“iksos daor” = is not).',
  },
  closestVerb: {
    args: ['word'],
    fi: (a: string[]) => `Verbistä ${a[0]} käytettiin lähintä löytynyttä muotoa.`,
    en: (a) => `The closest verb form found was used for ${a[0]}.`,
  },
  obligation: {
    args: [],
    fi: () => 'Velvollisuus: ensin infinitiivi, sitten se, jonka täytyy, genetiivissä ja lopuksi bēvilagon (”aō bēvilza” = sinun täytyy).',
    en: () => 'Obligation: first the infinitive, then the one who must in the genitive, then bēvilagon (“aō bēvilza” = you must).',
  },
  keptName: {
    args: ['word'],
    fi: (a: string[]) => `${a[0]} jätettiin nimeksi, koska sitä ei ole sanakirjassa.`,
    en: (a) => `${a[0]} was kept as a name, because the dictionary does not have it.`,
  },
  noFiGlosses: {
    args: [],
    fi: () => 'Sanakirjassa ei ole vielä suomenkielisiä merkityksiä, joten suomen sanoja ei voi vielä kääntää valyriaksi.',
    en: () => 'The dictionary has no Finnish meanings yet, so Finnish words cannot be translated into Valyrian.',
  },
  approxCase: {
    args: ['case'],
    fi: (a: string[]) =>
      `Suomen ${a[0] === 'translative' ? 'translatiiville' : 'abessiiville'} ei ole suoraa vastinetta valyriassa, joten käytettiin lähintä sijaa.`,
    en: (a) => `The Finnish ${a[0]} has no direct Valyrian case, so the closest case was used.`,
  },
  noHvWord: {
    args: ['words'],
    fi: (a: string[]) =>
      a[0]!.includes(',')
        ? `Näille sanoille ei löytynyt valyrian vastinetta: ${a[0]}. Ne on merkitty «näin».`
        : `Sanalle ${a[0]} ei löytynyt valyrian vastinetta. Se on merkitty «näin».`,
    en: (a) =>
      a[0]!.includes(',')
        ? `No Valyrian word was found for: ${a[0]}. They are marked «like this».`
        : `No Valyrian word was found for ${a[0]}. It is marked «like this».`,
  },
  noVerb: {
    args: [],
    fi: () => 'Lauseesta ei tunnistettu verbiä.',
    en: () => 'No verb was recognised.',
  },
  orderToHv: {
    args: [],
    fi: () => 'Sanajärjestys muutettiin valyrian mukaiseksi (verbi loppuun), ja persoonapronominit jätettiin pois.',
    en: () => 'The word order was changed to Valyrian (verb last), and subject pronouns were left out.',
  },
  orderFromHv: {
    args: [],
    fi: () => 'Sanajärjestys muutettiin: valyriassa verbi on lauseen lopussa, suomessa ja englannissa yleensä subjektin jälkeen.',
    en: () => 'The word order was changed: in Valyrian the verb comes last, in English it usually follows the subject.',
  },
  pastHabitual: {
    args: [],
    fi: () => 'Mennyt toistuva: vaihtoehdoissa on myös ”used to …” (oli tapana).',
    en: () => 'Past habitual: the alternatives also have “used to …”.',
  },
  hvNumeral: {
    args: [],
    fi: () =>
      'Lukusana muodostettiin valyrian lukusääntöjen mukaan: ykköset taipuvat substantiivin mukaan, ampa, kymmenet ja sadat eivät taivu, ja satojen jälkeen tulee ”se”.',
    en: () =>
      'The numeral was built by the Valyrian number rules: units agree with the noun, ampa, the tens and the hundreds do not inflect, and “se” follows the hundreds.',
  },
  passiveAgent: {
    args: [],
    fi: () => 'Passiivin tekijä (”by X”): tekijä genetiivissä ja sen perässä ondoso (usealle tekijälle ondossi).',
    en: () => 'The agent of a passive (“by X”): X in the genitive, followed by ondoso (ondossi for several).',
  },
  forBecause: {
    args: [],
    fi: () => '”For” lauseen alussa tarkoittaa ”koska”: kesrio syt.',
    en: () => '“For” opening a clause means “because”: kesrio syt.',
  },
  ofObject: {
    args: [],
    fi: () => 'Verbin jälkeinen ”of” luettiin niin, että sitä seuraava sana on verbin objekti.',
    en: () => '“Of” after a verb was read so that the word after it is the verb’s object.',
  },
  objectClause: {
    args: [],
    fi: () =>
      'Sanomista tai tietämistä tarkoittavan verbin objektina oleva lause tulee ennen verbiä (”Dārys issa vestris” = he sanovat, että hän on kuningas).',
    en: () => 'A clause that is the object of a verb of saying or knowing comes before the verb (“Dārys issa vestris” = they say he is a king).',
  },
  never: {
    args: [],
    fi: () => '”Never” on dōrī, ja verbi on myönteinen (ilman sanaa daor).',
    en: () => '“Never” is dōrī, with a positive verb (no daor).',
  },
  letInfinitive: {
    args: [],
    fi: () => '”Let X do”: X vokatiivissa ennen infinitiiviä (”Nūma dōros añuragon” = anna kiven jauhaa siemenet).',
    en: () => '“Let X do”: X in the vocative before the infinitive (“Nūma dōros añuragon” = let the stone grind up the seeds).',
  },
  age: {
    args: [],
    fi: () => 'Ikä: ”nähnyt N vuotta” – verbin urnegon perfekti ja kollektiivi jēdari.',
    en: () => 'Age: “has seen N years” – the perfect of urnegon and the collective jēdari.',
  },
  passive: {
    args: [],
    fi: () => '”Be” ja partisiippi: valyrian passiivi samassa aikamuodossa kuin ”be” (”Dārys sēntaks” = kuningas tapettiin).',
    en: () => '“Be” with a participle: the Valyrian passive, in the tense of “be” (“Dārys sēntaks” = the king was killed).',
  },
  obligationDative: {
    args: [],
    fi: () => 'Velvollisuus: se, jonka täytyy, on datiivissa, ja verbinä on bēvilagon infinitiivin kanssa (”aō bēvilza” = sinun täytyy).',
    en: () => 'Obligation: the one who must is in the dative, with bēvilagon and an infinitive (“aō bēvilza” = you must).',
  },
  obligationNoun: {
    args: [],
    fi: () => 'Velvollisuus, kun subjektina on substantiivi: tavallinen preesens, kuten ”Valar morghūlis”.',
    en: () => 'Obligation with a noun subject: the plain present, as in “Valar morghūlis”.',
  },
  subordinateSubjunctive: {
    args: [],
    fi: () => 'Sivulause (jos, kun, kunnes): verbi on subjunktiivissa (”Lo ōños ñāqot umbos” = jos valo pysyy).',
    en: () => 'Subordinate clause (if, when, until): the verb is in the subjunctive (“Lo ōños ñāqot umbos” = if the light remains).',
  },
  possession: {
    args: [],
    fi: () => '”Minulla on …” käännettiin verbillä emagon (omistaa), ja omistaja on lauseen subjekti.',
    en: () => '“Minulla on …” (possession) was translated with emagon (to have); the owner is the subject.',
  },
  permission: {
    args: [],
    fi: () => 'Lupa (”saat …”): datiivi ja infinitiivi (”Aōt vaodekuragon” = saat lähestyä).',
    en: () => 'Permission (“saat …”): dative and infinitive (“Aōt vaodekuragon” = you may approach).',
  },
  wish: {
    args: [],
    fi: () => 'Toive tai lupa (”-koon”, ”-kaamme”): datiivi ja infinitiivi (”Zijot māzigon” = tulkoon hän).',
    en: () => 'Wish or permission (“-koon”, “-kaamme”): dative and infinitive (“Zijot māzigon” = let him come).',
  },
  compound: {
    args: ['word', 'parts'],
    fi: (a: string[]) => `Yhdyssana ”${a[0]}” luettiin osista ${a[1]}.`,
    en: (a: string[]) => `The compound “${a[0]}” was read as ${a[1]}.`,
  },
  noForms: {
    args: ['word'],
    fi: (a: string[]) => `Sanalle ${a[0]} ei ole taivutusmuotoja, joten käytettiin perusmuotoa.`,
    en: (a: string[]) => `${a[0]} has no inflected forms, so its base form was used.`,
  },
  noParadigm: {
    args: ['word'],
    fi: (a: string[]) => `Sanalle ${a[0]} ei ole taivutustaulukkoa, joten käytettiin perusmuotoa.`,
    en: (a: string[]) => `${a[0]} has no inflection table, so its base form was used.`,
  },
  noConjugation: {
    args: ['word'],
    fi: (a: string[]) => `Verbille ${a[0]} ei ole taivutusmuotoja, joten käytettiin infinitiiviä.`,
    en: (a: string[]) => `The verb ${a[0]} has no conjugated forms, so the infinitive was used.`,
  },
  linker: {
    args: ['hv'],
    fi: (a: string[]) => `Lauseen aloittaa sidesana ${a[0]}, kuten valyriassa.`,
    en: (a: string[]) => `The clause opens with the linker ${a[0]}, as in Valyrian.`,
  },
  particle: {
    args: ['word'],
    fi: (a: string[]) => `”${a[0]}” luettiin osaksi verbiä.`,
    en: (a: string[]) => `“${a[0]}” was read as part of the verb.`,
  },
  numeralKept: {
    args: ['word'],
    fi: (a: string[]) => `”${a[0]}” jätettiin sellaisenaan, koska sille ei ole valyrian lukusanaa.`,
    en: (a: string[]) => `“${a[0]}” was kept as written: there is no Valyrian numeral for it.`,
  },
  beVerb: {
    args: ['word', 'hv'],
    fi: (a: string[]) => `”be ${a[0]}” on valyriassa yksi verbi: ${a[1]}.`,
    en: (a: string[]) => `“be ${a[0]}” is one verb in Valyrian: ${a[1]}.`,
  },
  infinitive: {
    args: ['word', 'hv'],
    fi: (a: string[]) => `”${a[0]}” käännettiin infinitiivillä ${a[1]}.`,
    en: (a: string[]) => `“${a[0]}” was translated with the infinitive ${a[1]}.`,
  },
  valencyCase: {
    args: ['word', 'case'],
    fi: (a: string[]) => `Verbin ${a[0]} objekti on ${CASE_IN_FI[a[1]!] ?? a[1]}, kuten sanakirjan merkinnässä sanotaan.`,
    en: (a: string[]) => `The verb ${a[0]} takes its object in the ${CASE_IN_EN[a[1]!] ?? a[1]}, as its dictionary entry says.`,
  },
});

declare module '../note-defs' {
  interface NoteIds extends Record<keyof typeof notes, true> {}
}
